/**
 * EtsyShippingProfileSelector — bloc Settings → Atelier qui permet à
 * Stéphanie de choisir le profile Etsy par défaut utilisé pour les
 * publications `limited`/`original` (expédition atelier — cf. PR-B).
 *
 * Comportement :
 *   - Si Etsy connecté → dropdown alimentée par
 *     `GET /api/platform-connections/{id}/etsy-shipping-profiles` (lecture
 *     live Etsy API).
 *   - Si Etsy déconnecté OU API Etsy en erreur (503 token expiré, 5xx) →
 *     fallback input number (saisie manuelle de l'ID).
 *   - Save → `PUT /api/atelier-settings/etsy-default-shipping-profile`.
 *
 * Le setting persisté est un KV plat `app_settings.etsy_default_shipping_profile_id`
 * (consommé par `etsy/adapter.publish` pour limited/original ET par
 * `etsy_shipping_bindings_service` comme fallback global POD).
 */

import { useMemo, useState } from 'react'
import { Loader2, ExternalLink } from 'lucide-react'
import { toast } from 'sonner'

import Button from '@/components/ui/Button'
import Field from '@/components/ui/Field'
import Input from '@/components/ui/Input'
import Select from '@/components/ui/Select'
import {
  useEtsyDefaultShippingProfile,
  useEtsyShippingProfiles,
  useUpdateEtsyDefaultShippingProfile,
} from '@/hooks/useAtelierSettings'
import { usePlatformConnections } from '@/hooks/usePlatformConnections'

export default function EtsyShippingProfileSelector() {
  const connectionsQuery = usePlatformConnections()
  const settingQuery = useEtsyDefaultShippingProfile()
  const updateMutation = useUpdateEtsyDefaultShippingProfile()

  const etsyConnection = useMemo(() => {
    const conns = connectionsQuery.data ?? []
    return conns.find((c) => c.platform === 'etsy' && !c.revoked_at) ?? null
  }, [connectionsQuery.data])

  const profilesQuery = useEtsyShippingProfiles(etsyConnection?.id ?? null)

  // État local du formulaire : ID sélectionné dans la dropdown OU saisi
  // dans l'input fallback. `null` = pas de profile choisi (clear).
  // Pattern « adjust state when prop changes » (React 19) — pas de
  // useEffect+setState pour éviter cascading renders, on compare la
  // valeur précédente du serveur et resync uniquement quand elle change.
  const [selectedId, setSelectedId] = useState<number | null>(settingQuery.data?.value ?? null)
  const [manualInput, setManualInput] = useState<string>(
    settingQuery.data?.value !== null && settingQuery.data?.value !== undefined
      ? String(settingQuery.data.value)
      : '',
  )
  const [prevServerValue, setPrevServerValue] = useState<number | null | undefined>(
    settingQuery.data?.value,
  )
  if (settingQuery.data && settingQuery.data.value !== prevServerValue) {
    setPrevServerValue(settingQuery.data.value)
    setSelectedId(settingQuery.data.value)
    setManualInput(settingQuery.data.value !== null ? String(settingQuery.data.value) : '')
  }

  const isLoading = connectionsQuery.isLoading || settingQuery.isLoading
  const profilesUnavailable = etsyConnection !== null && profilesQuery.isError
  const showFallbackInput = etsyConnection === null || profilesUnavailable

  const handleSave = () => {
    const valueToPost = showFallbackInput
      ? manualInput.trim() === ''
        ? null
        : Number.parseInt(manualInput, 10)
      : selectedId
    if (valueToPost !== null && (!Number.isFinite(valueToPost) || valueToPost < 1)) {
      toast.error('ID profile invalide — saisis un entier ≥ 1')
      return
    }
    updateMutation.mutate(
      { value: valueToPost },
      {
        onSuccess: () => {
          toast.success(
            valueToPost === null
              ? "Profile d'expédition atelier effacé"
              : `Profile d'expédition atelier mis à jour (#${valueToPost})`,
          )
        },
        onError: (err) => {
          toast.error(err.detail ?? 'Sauvegarde impossible')
        },
      },
    )
  }

  if (isLoading) {
    return (
      <div>
        <header className="settings-section-header">
          <div>
            <h2 className="ui-h4 settings-section-title">Profile d'expédition atelier (Etsy)</h2>
          </div>
        </header>
        <p className="atelier-shipping-loading">
          <Loader2 className="atelier-shipping-spinner" size={16} />
          Chargement…
        </p>
      </div>
    )
  }

  return (
    <div>
      <header className="settings-section-header">
        <div>
          <h2 className="ui-h4 settings-section-title">Profile d'expédition atelier (Etsy)</h2>
          <p className="ui-body ui-text-muted settings-section-subtitle">
            Utilisé pour publier les variantes <strong>tirage limité</strong> et{' '}
            <strong>œuvre originale</strong> que tu expédies toi-même depuis l'atelier (Colissimo,
            lettre suivie, …). Les variantes <strong>open edition</strong> (POD Gelato) ont leur
            propre profile auto-synchronisé par SKU.
          </p>
        </div>
      </header>

      {etsyConnection === null && (
        <div className="ui-alert ui-alert--info" role="status">
          Etsy n'est pas connecté. Connecte ton compte Etsy depuis l'onglet{' '}
          <strong>Diffusion</strong> pour voir la liste des profiles disponibles — sinon, saisis
          manuellement l'ID du profile ci-dessous.
        </div>
      )}

      {profilesUnavailable && (
        <div className="ui-alert ui-alert--info" role="status">
          Impossible de lire les profiles depuis Etsy (token expiré ou API indisponible). Reconnecte
          Etsy depuis l'onglet <strong>Diffusion</strong>, ou saisis l'ID manuellement ci-dessous.
        </div>
      )}

      <div className="atelier-shipping-fieldset">
        {!showFallbackInput && profilesQuery.data && (
          <Field
            label="Profile d'expédition par défaut"
            helper="Sélectionne le profile que tu as créé pour tes expéditions atelier."
          >
            {(controlProps) => (
              <Select
                {...controlProps}
                value={selectedId === null ? '' : String(selectedId)}
                onChange={(e) =>
                  setSelectedId(e.target.value === '' ? null : Number.parseInt(e.target.value, 10))
                }
              >
                <option value="">— Aucun profile —</option>
                {profilesQuery.data.map((p) => (
                  <option key={p.shipping_profile_id} value={p.shipping_profile_id}>
                    {p.title}
                    {p.origin_country_iso ? ` (${p.origin_country_iso})` : ''}
                    {p.processing_days_display_label ? ` — ${p.processing_days_display_label}` : ''}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}

        {showFallbackInput && (
          <>
            <Field
              label="ID du profile Etsy"
              helper="Saisis l'identifiant numérique du profile (visible côté Etsy seller dans l'URL de gestion du profile)."
            >
              {(controlProps) => (
                <Input
                  {...controlProps}
                  type="number"
                  min={1}
                  value={manualInput}
                  onChange={(e) => setManualInput(e.target.value)}
                  placeholder="ex: 123456789"
                />
              )}
            </Field>
            <p className="atelier-shipping-link">
              <a
                href="https://www.etsy.com/your/shops/me/shipping/profiles"
                target="_blank"
                rel="noreferrer noopener"
              >
                Ouvrir mes profiles Etsy
                <ExternalLink size={12} />
              </a>
            </p>
          </>
        )}

        <div className="atelier-shipping-actions">
          <Button onClick={handleSave} disabled={updateMutation.isPending}>
            {updateMutation.isPending ? 'Sauvegarde…' : 'Enregistrer'}
          </Button>
        </div>
      </div>
    </div>
  )
}
